import { ArrowRightIcon, CheckIcon, PlusIcon } from '@heroicons/react/outline';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';

import finishing from '../finishing/finishing.module.css';
import styles from './editing.module.css';

import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import {
  SEEDANCE_25_EDIT_CREDITS_PER_SECOND,
  SEEDANCE_25_EDIT_MAX_SECONDS,
  SEEDANCE_25_EDIT_MIN_SECONDS,
  SEEDANCE_25_EDIT_TURBO_CREDITS_PER_SECOND,
  WAN_30_EDIT_CREDITS_PER_SECOND,
  WAN_30_EDIT_MAX_SECONDS,
  WAN_30_EDIT_MIN_SECONDS,
} from '@/constants/videoModels';
import { VIDEO_TOOL_ROUTES } from '@/constants/videoTools';

const CDN = 'https://media.easy-peasy.ai/landing/video-edit';

/**
 * Before/after pairs from Seedance 2.5's official editing showcase (BytePlus
 * ModelArk playground templates), re-hosted on our CDN — the same posture as
 * the "official-*" clips on the Seedance 2.5 page. Each entry is the exact
 * instruction the model was given, lightly shortened for the card.
 */
interface EditExample {
  id: string;
  title: string;
  tag: string;
  instruction: string;
  refs?: string[];
  seconds: number;
}

const EXAMPLES: EditExample[] = [
  {
    id: '29',
    title: 'Swap in a different actor',
    tag: 'Replace a person',
    instruction:
      'Replace the seated actor with the person from @Image1 or @Image2.',
    refs: ['edit-ref-29-image-1.webp', 'edit-ref-29-image-2.webp'],
    seconds: 30,
  },
  {
    id: '27',
    title: 'Clear the crowd, keep the lead',
    tag: 'Remove people',
    instruction: 'Remove all people in the video except the protagonist.',
    seconds: 17,
  },
  {
    id: '09',
    title: 'Erase the drone from the safari shot',
    tag: 'Remove an object',
    instruction:
      'Remove the drone and the foreground vehicle edge, and inpaint the gaps. Keep the giraffes, branches, golden backlight and camera composition unchanged.',
    seconds: 8,
  },
  {
    id: '30',
    title: 'Move the lizard into a new terrarium',
    tag: 'Change the scene',
    instruction:
      'Change the scene to a breeding incubator with stones and moss — refer to @Image1.',
    refs: ['edit-ref-30-image-1.webp'],
    seconds: 20,
  },
  {
    id: '31',
    title: 'An army arrives on cue',
    tag: 'Timestamped insert',
    instruction:
      'From 0s to 6s: insert @Image1 (the monster army) on the open lawn, charging toward the protagonist. From 16s to 20s: the army is knocked down by a blast of air. Everything else unchanged.',
    refs: ['edit-ref-31-image-1.webp'],
    seconds: 25,
  },
  {
    id: '28',
    title: 'A steam train bursts out of the screen',
    tag: 'Add something new',
    instruction:
      'Keep the black-and-white cinema footage intact; a steam locomotive breaks through the screen into the hall, and the picture snaps to full colour at that instant.',
    seconds: 14,
  },
];

/** Renders @Image1..@Image4 in a translated sentence as inline code chips. */
const withImageTags = (text: string) =>
  text
    .split(/(@Image\d)/)
    .map((part, index) =>
      /^@Image\d$/.test(part) ? <code key={index}>{part}</code> : part,
    );

const QUESTIONS: [string, string][] = [
  [
    'What can the AI video editor change?',
    'Anything you can describe: replace a person or object using a reference photo, remove people or props, change the background or setting, add something new at a specific moment, or change lighting, weather and style. The rest of the shot — framing, camera moves, pacing and audio — stays as it was.',
  ],
  [
    'How do I replace a person in a video with someone from a photo?',
    'Upload the clip, add up to four reference images, and write an edit such as "replace the man with @Image1". Seedance 2.5 keeps the pose, motion and lighting of the original performance and renders the new person into it. Fidelity to the photo varies shot to shot, so keep the reference well-lit and front-facing.',
  ],
  [
    'How long can the video be?',
    'Seedance 2.5 edits clips from 4 to 30 seconds. WAN 3.0 takes 2 to 15 seconds. Uploads can be MP4 or MOV up to 100 MB, and you can also edit any video you generated on Easy-Peasy.AI straight from its menu.',
  ],
  [
    'What are timestamped edits?',
    'Every edit can be limited to a time range, set by dragging handles on the timeline. Ranges snap to whole seconds — the finest timing the models read — so "from 3s to 8s: change the sky to sunset" applies only there.',
  ],
  [
    'How much does an edit cost?',
    'Editing is billed per second of the source clip, because both providers process the input and the output. Seedance 2.5 is 15 credits per second at 720p and 7 at 480p; Seedance Fast is 9 at 720p and 10 at 1080p; WAN 3.0 is 10 at 720p, 5 at 480p and 20 at 1080p. The exact total is shown before you apply.',
  ],
  [
    'Does it work with real people in the footage?',
    'Yes. One of our providers declines any clip or reference showing a real person, so those jobs are rerouted automatically to a provider that accepts them, at the same price. Deepfakes, impersonation and sexual content involving real people remain prohibited.',
  ],
  [
    'Will the edit change the length or aspect ratio?',
    'No. The edited video keeps the original duration, framing and aspect ratio, and comes back as a separate file — your original stays in your library untouched.',
  ],
];

function BeforeAfter({
  example,
  eager,
  t,
}: {
  example: EditExample;
  eager?: boolean;
  t: (key: string) => string;
}) {
  const [side, setSide] = useState<'before' | 'after'>('after');
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = `${CDN}/edit-${example.id}-${side}.mp4`;
  const poster = `${CDN}/edit-${example.id}-${side}-poster.jpg`;

  // Switching sides keeps the playhead so the two versions line up frame for
  // frame — that is the whole point of the comparison.
  const switchSide = (next: 'before' | 'after') => {
    const current = videoRef.current?.currentTime ?? 0;
    setSide(next);
    requestAnimationFrame(() => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = current;
      if (playing) video.play().catch(() => undefined);
    });
  };

  useEffect(() => {
    if (playing) videoRef.current?.play().catch(() => undefined);
  }, [playing, side]);

  return (
    <div className={styles.compare}>
      {playing ? (
        <video
          key={src}
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          loop
          playsInline
          preload='auto'
          controls
        />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={poster}
          alt={t(example.title)}
          loading={eager ? 'eager' : 'lazy'}
          width={960}
          height={540}
        />
      )}
      <div
        className={styles.compareTabs}
        role='group'
        aria-label={t('Compare')}
      >
        {(['before', 'after'] as const).map((value) => (
          <button
            key={value}
            type='button'
            aria-pressed={side === value}
            onClick={() => switchSide(value)}
          >
            {t(value === 'before' ? 'Before' : 'After')}
          </button>
        ))}
      </div>
      {!playing && (
        <button
          type='button'
          className={styles.comparePlay}
          onClick={() => setPlaying(true)}
          aria-label={t('Play the example')}
        >
          <span aria-hidden='true'>▶</span>
        </button>
      )}
      {example.refs && !playing && (
        <div className={styles.compareRefs}>
          {example.refs.map((ref, index) => (
            <div key={ref}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={`${CDN}/${ref}`} alt='' loading='lazy' />
              <span>@Image{index + 1}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VideoEditLanding() {
  const { t: translate } = useTranslation('video-finishing');
  const { locale = 'en' } = useRouter();
  const t = (key: string, values?: Record<string, string | number>) =>
    translate(key, values, { default: key, ns: 'video-finishing' });
  const route = VIDEO_TOOL_ROUTES.edit;
  const prefix = locale === 'en' ? '' : `/${locale}`;
  const canonical = `https://easy-peasy.ai${prefix}${route.landing}`;
  const name = 'Edit Video with AI';
  const title =
    'Edit Video with AI — Replace, Remove & Change Anything in a Clip';
  const description =
    'Edit videos by describing the change. Replace a person from a photo, remove objects or people, change the scene, or add something at an exact moment. Seedance 2.5 and WAN 3.0, up to 30 seconds, credits shown before you start.';
  const cta = t('Edit your video');
  const hero = EXAMPLES[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonical}#page`,
        url: canonical,
        name: t(title),
        description: t(description),
        inLanguage: locale,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Easy-Peasy.AI',
            item: `https://easy-peasy.ai${prefix}`,
          },
          { '@type': 'ListItem', position: 2, name: t(name), item: canonical },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: QUESTIONS.map(([q, a]) => ({
          '@type': 'Question',
          name: t(q),
          acceptedAnswer: { '@type': 'Answer', text: t(a) },
        })),
      },
    ],
  };

  const engines = [
    {
      name: 'Seedance 2.5',
      blurb:
        'The flagship. Best fidelity on people and motion, the widest window, and the one to pick for replacing a subject from a photo.',
      window: `${SEEDANCE_25_EDIT_MIN_SECONDS}–${SEEDANCE_25_EDIT_MAX_SECONDS}s`,
      resolutions: '480p · 720p',
      price: SEEDANCE_25_EDIT_CREDITS_PER_SECOND['720p'],
      priceNote: [[SEEDANCE_25_EDIT_CREDITS_PER_SECOND['480p'], 'at 480p']],
    },
    {
      name: 'Seedance Fast',
      blurb:
        'The same model on the faster tier — roughly half the credits, and the only Seedance option that renders 1080p.',
      window: `${SEEDANCE_25_EDIT_MIN_SECONDS}–${SEEDANCE_25_EDIT_MAX_SECONDS}s`,
      resolutions: '720p · 1080p',
      price: SEEDANCE_25_EDIT_TURBO_CREDITS_PER_SECOND['720p'],
      priceNote: [
        [SEEDANCE_25_EDIT_TURBO_CREDITS_PER_SECOND['1080p'], 'at 1080p'],
      ],
    },
    {
      name: 'WAN 3.0',
      blurb:
        'Alibaba’s model, a different family with its own strengths — a good second opinion when Seedance declines a clip, and 480p to 1080p.',
      window: `${WAN_30_EDIT_MIN_SECONDS}–${WAN_30_EDIT_MAX_SECONDS}s`,
      resolutions: '480p · 720p · 1080p',
      price: WAN_30_EDIT_CREDITS_PER_SECOND['720p'],
      priceNote: [
        [WAN_30_EDIT_CREDITS_PER_SECOND['480p'], 'at 480p'],
        [WAN_30_EDIT_CREDITS_PER_SECOND['1080p'], 'at 1080p'],
      ],
    },
  ];

  return (
    <div
      className={finishing.page}
      lang={locale}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <Seo
        title={`${t(title)} | Easy-Peasy.AI`}
        description={t(description)}
        image={`${CDN}/edit-29-after-poster.jpg`}
        page='other'
        structuredData={false}
      />
      <Head>
        <script
          key='video-edit-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <a href='#main-content' className={finishing.skip}>
        {t('Skip to content')}
      </a>
      <header className={finishing.header}>
        <LandingPageNavigation />
      </header>
      <main id='main-content'>
        <section className={`${finishing.hero} ${finishing.container}`}>
          <div className={finishing.heroCopy}>
            <nav aria-label={t('Breadcrumb')} className={finishing.breadcrumb}>
              <Link href='/ai-video-generator'>{t('Video studio')}</Link>
              <span aria-hidden='true'>/</span>
              <span>{t('Edit')}</span>
            </nav>
            <h1>
              <span className={finishing.eyebrow}>{t(name)}</span>
              <span className={finishing.heroTitle}>
                {t('Change the shot.')}
                <br />
                <em>{t('Keep the take.')}</em>
              </span>
            </h1>
            <p className={finishing.lead}>
              {t(
                'Point at a moment and describe what should be different. Swap in a person from a photo, clear out a crowd, move the scene somewhere else, or add something new on cue — everything else stays exactly as you shot it.',
              )}
            </p>
            <div className={finishing.actions}>
              <Link href={route.workspace} className={finishing.primary}>
                {cta}
                <ArrowRightIcon aria-hidden='true' />
              </Link>
              <a href='#examples' className={finishing.textLink}>
                {t('See what it can do')}
                <span aria-hidden='true'>↓</span>
              </a>
            </div>
            <p className={finishing.finePrint}>
              {t(
                'Paid plans · Credits shown before you start · Original file kept',
              )}
            </p>
          </div>
          <div className={finishing.heroVisual}>
            <div className={finishing.previewTop}>
              <span className={finishing.liveDot} />
              {t('One instruction, two reference photos')}
              <span className={finishing.previewIndex}>01 / 06</span>
            </div>
            <BeforeAfter example={hero} eager t={t} />
            <div className={styles.heroInstruction}>
              <span>{t('The edit, as written')}</span>
              {withImageTags(t(hero.instruction))}
            </div>
          </div>
        </section>

        <div className={finishing.promiseBand}>
          <div className={finishing.container}>
            {[
              'Clips up to 30 seconds',
              'Seedance 2.5 & WAN 3.0',
              'Timestamped edits',
              'Up to 4 reference photos',
            ].map((value) => (
              <span key={value}>
                <CheckIcon aria-hidden='true' />
                {t(value)}
              </span>
            ))}
          </div>
        </div>

        <section
          id='examples'
          className={`${finishing.container} ${styles.examples}`}
        >
          <div className={finishing.sectionHeading}>
            <div>
              <span className={finishing.eyebrow}>{t('Before and after')}</span>
              <h2>{t('Six edits.\nSame footage, different film.')}</h2>
            </div>
            <p>
              {t(
                'Official Seedance 2.5 editing examples. Toggle each one between the original and the result — the playhead stays in place so you can compare frame for frame.',
              )}
            </p>
          </div>
          <div className={styles.examplesGrid}>
            {EXAMPLES.map((example) => (
              <article key={example.id} className={styles.example}>
                <BeforeAfter example={example} t={t} />
                <span className={styles.exampleTag}>{t(example.tag)}</span>
                <h3>{t(example.title)}</h3>
                <p>
                  <strong>{t('Instruction')}:</strong> {t(example.instruction)}
                </p>
              </article>
            ))}
          </div>
          <p className={styles.credit}>
            {t('Example footage from the Seedance 2.5 showcase by')}{' '}
            <a
              href='https://www.byteplus.com/en/product/ModelArk'
              target='_blank'
              rel='noopener noreferrer'
            >
              BytePlus ModelArk
            </a>
            .
          </p>
        </section>

        <section className={`${finishing.container} ${finishing.intro}`}>
          <div>
            <span className={finishing.eyebrow}>
              {t('Made for the footage you already have')}
            </span>
            <h2>{t('Fix the shot\ninstead of reshooting it.')}</h2>
          </div>
          <div className={finishing.editorialList}>
            {[
              [
                'Replace a person or a product',
                'Attach a photo and write "replace the presenter with @Image1". The pose, motion and lighting of the original performance carry over to the new subject.',
              ],
              [
                'Remove what should not be there',
                'A passer-by, a drone, a logo, on-screen text, the background music. Name it and it is gone, with the gap filled in to match the surroundings.',
              ],
              [
                'Change the scene or the mood',
                'Move the shot to a new location from a reference image, turn noon into golden hour, or restyle the whole clip — without touching the action.',
              ],
              [
                'Add something at the right moment',
                'Drag a range on the timeline and describe what enters the frame there. Ranges snap to whole seconds, the finest timing the models read.',
              ],
            ].map(([heading, body], i) => (
              <article key={heading}>
                <span className={finishing.number}>0{i + 1}</span>
                <div>
                  <h3>{t(heading)}</h3>
                  <p>{t(body)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id='models' className={finishing.modelsSection}>
          <div className={finishing.container}>
            <div className={finishing.sectionHeading}>
              <div>
                <span className={finishing.eyebrow}>
                  {t('Two model families')}
                </span>
                <h2>{t('Pick the engine\nfor the job.')}</h2>
              </div>
              <p>
                {t(
                  'Editing is billed per second of the clip you upload, because both the input and the output are processed. The total is shown before you apply.',
                )}
              </p>
            </div>
            <div className={styles.engines}>
              {engines.map((engine) => (
                <div key={engine.name} className={styles.engine}>
                  <h3>{engine.name}</h3>
                  <p>{t(engine.blurb)}</p>
                  <div className={styles.engineFacts}>
                    <div>
                      <span>{t('Clip length')}</span>
                      <span>{engine.window}</span>
                    </div>
                    <div>
                      <span>{t('Output')}</span>
                      <span>{engine.resolutions}</span>
                    </div>
                    <div>
                      <span>{t('Reference photos')}</span>
                      <span>{t('Up to 4')}</span>
                    </div>
                  </div>
                  <div className={styles.enginePrice}>
                    {engine.price}
                    <small>
                      {t('credits / second at 720p')}
                      {(engine.priceNote as [number, string][]).map(
                        ([credits, label]) => ` · ${credits} ${t(label)}`,
                      )}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id='how-it-works'
          className={`${finishing.container} ${finishing.steps}`}
        >
          <div className={finishing.sectionHeading}>
            <div>
              <span className={finishing.eyebrow}>
                {t('From upload to final cut')}
              </span>
              <h2>{t('Three steps. One edited video.')}</h2>
            </div>
            <Link href={route.workspace} className={finishing.textLink}>
              {cta}
              <ArrowRightIcon aria-hidden='true' />
            </Link>
          </div>
          <ol>
            {[
              [
                t('Bring your video'),
                t(
                  'Upload an MP4 or MOV up to 100 MB, or open any video you generated here from its menu with "Edit with AI".',
                ),
              ],
              [
                t('Describe the change'),
                t(
                  'Add an edit, set its time range on the timeline, and attach reference photos to name as @Image1 to @Image4. Add up to six edits in one pass.',
                ),
              ],
              [
                t('Save the new result'),
                t(
                  'Pick the engine and resolution, check the credit total, and apply. The edited clip lands in your library as a new video — the original is untouched.',
                ),
              ],
            ].map(([heading, body], i) => (
              <li key={heading}>
                <span className={finishing.stepNumber}>0{i + 1}</span>
                <h3>{heading}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className={finishing.faqSection}>
          <div className={`${finishing.container} ${finishing.faqLayout}`}>
            <div>
              <span className={finishing.eyebrow}>
                {t('A few things worth knowing')}
              </span>
              <h2>{t('Before you\nhit apply.')}</h2>
              <p>
                {t(
                  'Straight answers to help you choose the right tool for your video.',
                )}
              </p>
              <Link href='/contact' className={finishing.textLink}>
                {t('Talk to our team')} →
              </Link>
            </div>
            <div>
              {QUESTIONS.map(([question, answer]) => (
                <details key={question}>
                  <summary>
                    {t(question)}
                    <PlusIcon aria-hidden='true' />
                  </summary>
                  <p>{t(answer)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className={`${finishing.container} ${finishing.finalCta}`}>
          <span className={finishing.eyebrow}>
            {t('The take is already good')}
          </span>
          <h2>{t('Now make it\nthe one you wanted.')}</h2>
          <Link href={route.workspace} className={finishing.primary}>
            {cta}
            <ArrowRightIcon aria-hidden='true' />
          </Link>
          <p>
            {t(
              'A dedicated workspace. A clear credit quote. Your original file kept.',
            )}
          </p>
        </section>

        <nav
          aria-label={t('Related video tools')}
          className={`${finishing.container} ${finishing.related}`}
        >
          <Link href={VIDEO_TOOL_ROUTES.extend.landing}>
            <span>{t('Need a longer scene?')}</span>
            <strong>{t('Explore AI Video Extender')} ↗</strong>
          </Link>
          <Link href={VIDEO_TOOL_ROUTES.upscale.landing}>
            <span>{t('Ready for a sharper finish?')}</span>
            <strong>{t('Explore AI Video Upscaler')} ↗</strong>
          </Link>
          <Link href='/ai-video-generator/seedance-2-5'>
            <span>{t('Meet the model behind it')}</span>
            <strong>{t('Explore Seedance 2.5')} ↗</strong>
          </Link>
        </nav>
      </main>
      <Footer />
    </div>
  );
}
